import {useCallback, useEffect, useRef} from 'react';

import {KEYBOARD_EVENT_KEY} from '../constants';

type ArrowKeyListNavigationOptions = {
    containerRef: React.RefObject<HTMLElement | null>;
    itemSelector: string;
    isEnabled?: boolean;
    loop?: boolean;
    scrollIntoViewOnFocus?: boolean;
    keepOriginFocusOnNavigate?: boolean;
    activateActiveItemOnEnter?: boolean;
    onActivateActiveItem?: (item: HTMLElement) => void;
};

/**
 * Enables ArrowUp/ArrowDown keyboard navigation across focusable items in a container.
 *
 * @example
 * const listRef = useRef<HTMLDivElement>(null);
 * useArrowKeyListNavigation({
 *   containerRef: listRef,
 *   itemSelector: '[data-keyboard-nav-item]',
 *   isEnabled: true,
 *   loop: true,
 *   scrollIntoViewOnFocus: true,
 *   keepOriginFocusOnNavigate: false,
 *   activateActiveItemOnEnter: false,
 * });
 */
export const useArrowKeyListNavigation = ({
    containerRef,
    itemSelector,
    isEnabled = true,
    loop = true,
    scrollIntoViewOnFocus = true,
    keepOriginFocusOnNavigate = false,
    activateActiveItemOnEnter = false,
    onActivateActiveItem,
}: ArrowKeyListNavigationOptions) => {
    const focusedItemRef = useRef<HTMLElement | null>(null);
    const navigationOriginRef = useRef<HTMLElement | null>(null);

    /**
     * Removes the active descendant relationship from the navigation origin.
     */
    const clearNavigationOrigin = useCallback(() => {
        navigationOriginRef.current?.removeAttribute('aria-activedescendant');
        navigationOriginRef.current = null;
    }, []);

    const setActiveItem = useCallback((nextItem: HTMLElement) => {
        focusedItemRef.current?.removeAttribute('data-keyboard-nav-active');
        nextItem.setAttribute('data-keyboard-nav-active', 'true');
        focusedItemRef.current = nextItem;

        if (navigationOriginRef.current && nextItem.id) {
            navigationOriginRef.current.setAttribute(
                'aria-activedescendant',
                nextItem.id,
            );
        }
    }, []);

    /**
     * Returns true when the keyboard event originated from a text-editable element.
     */
    const isTextEditableTarget = useCallback(
        (eventTarget: EventTarget | null) => {
            if (!(eventTarget instanceof HTMLElement)) {
                return false;
            }

            const tagName = eventTarget.tagName.toLowerCase();
            return (
                tagName === 'input' ||
                tagName === 'textarea' ||
                eventTarget.isContentEditable
            );
        },
        [],
    );

    /**
     * Returns navigable elements in document order from the container.
     */
    const getItems = useCallback(() => {
        const container = containerRef.current;
        if (!container || itemSelector.trim().length === 0) {
            return [] as HTMLElement[];
        }

        return Array.from(
            container.querySelectorAll<HTMLElement>(itemSelector),
        );
    }, [containerRef, itemSelector]);

    /**
     * Returns the current index for keyboard navigation based on active or remembered focus.
     */
    const getCurrentIndex = useCallback(
        (items: HTMLElement[]) => {
            if (items.length === 0) {
                return -1;
            }

            const activeElement = document.activeElement as HTMLElement | null;
            const activeItem =
                activeElement?.closest<HTMLElement>(itemSelector);
            if (activeItem) {
                const activeIndex = items.indexOf(activeItem);
                if (activeIndex !== -1) {
                    return activeIndex;
                }
            }

            if (!focusedItemRef.current) {
                return -1;
            }

            return items.indexOf(focusedItemRef.current);
        },
        [itemSelector],
    );

    /**
     * Moves focus to the next or previous item according to keyboard intent.
     */
    const moveFocus = useCallback(
        (direction: 'next' | 'previous', shouldKeepOriginFocus: boolean) => {
            const items = getItems();
            if (items.length === 0) {
                return;
            }

            const currentIndex = getCurrentIndex(items);
            const fallbackIndex = direction === 'next' ? -1 : 0;
            const safeCurrentIndex =
                currentIndex === -1 ? fallbackIndex : currentIndex;

            const nextIndex =
                direction === 'next'
                    ? safeCurrentIndex + 1
                    : safeCurrentIndex - 1;

            const boundedIndex = loop
                ? (nextIndex + items.length) % items.length
                : Math.min(Math.max(nextIndex, 0), items.length - 1);

            const nextItem = items[boundedIndex];
            if (!nextItem) {
                return;
            }

            setActiveItem(nextItem);

            if (!shouldKeepOriginFocus) {
                nextItem.focus({preventScroll: true});
            }

            if (
                scrollIntoViewOnFocus &&
                typeof nextItem.scrollIntoView === 'function'
            ) {
                nextItem.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        },
        [getCurrentIndex, getItems, loop, scrollIntoViewOnFocus, setActiveItem],
    );

    useEffect(() => {
        if (!isEnabled || itemSelector.trim().length === 0) {
            return;
        }

        const handleFocusIn = (event: FocusEvent) => {
            const container = containerRef.current;
            if (!container) {
                return;
            }

            const eventTarget = event.target;
            if (!(eventTarget instanceof HTMLElement)) {
                return;
            }

            if (
                navigationOriginRef.current &&
                eventTarget !== navigationOriginRef.current
            ) {
                clearNavigationOrigin();
            }

            const focusedItem = eventTarget.closest<HTMLElement>(itemSelector);
            if (!focusedItem || !container.contains(focusedItem)) {
                return;
            }

            setActiveItem(focusedItem);
        };

        document.addEventListener('focusin', handleFocusIn);

        return () => {
            document.removeEventListener('focusin', handleFocusIn);
        };
    }, [
        clearNavigationOrigin,
        containerRef,
        isEnabled,
        itemSelector,
        setActiveItem,
    ]);

    useEffect(() => {
        if (!isEnabled || itemSelector.trim().length === 0) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === KEYBOARD_EVENT_KEY.enter &&
                activateActiveItemOnEnter
            ) {
                const container = containerRef.current;
                const activeItem = focusedItemRef.current;
                if (
                    !container ||
                    !activeItem ||
                    !container.contains(activeItem)
                ) {
                    return;
                }

                const activeElement =
                    document.activeElement instanceof HTMLElement
                        ? document.activeElement
                        : null;
                const focusedNavigationItem =
                    activeElement?.closest<HTMLElement>(itemSelector);
                const isNavigationOriginFocused =
                    keepOriginFocusOnNavigate &&
                    activeElement === navigationOriginRef.current &&
                    isTextEditableTarget(activeElement);
                const isFocusInsideContainerOutsideItem =
                    Boolean(
                        activeElement && container.contains(activeElement),
                    ) && !focusedNavigationItem;

                if (
                    !isNavigationOriginFocused &&
                    !isFocusInsideContainerOutsideItem
                ) {
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                if (onActivateActiveItem) {
                    onActivateActiveItem(activeItem);
                    return;
                }
                activeItem.click();
                return;
            }

            if (
                event.key !== KEYBOARD_EVENT_KEY.arrowDown &&
                event.key !== KEYBOARD_EVENT_KEY.arrowUp
            ) {
                return;
            }

            if (event.repeat) {
                return;
            }

            const container = containerRef.current;
            if (!container) {
                return;
            }

            const activeElement = document.activeElement as HTMLElement | null;
            const eventTarget = event.target as Node | null;
            const hasExistingListFocus =
                activeElement?.closest<HTMLElement>(itemSelector) !== null ||
                focusedItemRef.current !== null;
            const isInsideContainer =
                Boolean(activeElement && container.contains(activeElement)) ||
                Boolean(eventTarget && container.contains(eventTarget));
            const isFromTextInput = isTextEditableTarget(event.target);
            const shouldKeepOriginFocus =
                keepOriginFocusOnNavigate && isFromTextInput;
            const canNavigateFromOrigin =
                isFromTextInput &&
                (keepOriginFocusOnNavigate || !hasExistingListFocus);

            if (!isInsideContainer && !canNavigateFromOrigin) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (shouldKeepOriginFocus) {
                navigationOriginRef.current = activeElement;
            } else {
                clearNavigationOrigin();
            }

            if (event.key === KEYBOARD_EVENT_KEY.arrowDown) {
                moveFocus('next', shouldKeepOriginFocus);
                return;
            }

            moveFocus('previous', shouldKeepOriginFocus);
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            clearNavigationOrigin();
        };
    }, [
        clearNavigationOrigin,
        containerRef,
        isEnabled,
        itemSelector,
        keepOriginFocusOnNavigate,
        activateActiveItemOnEnter,
        onActivateActiveItem,
        isTextEditableTarget,
        moveFocus,
    ]);
};
